import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtCurrency, fmtDate, fmtDateTime } from "@/utils/format";
import { Receipt, FileDown, Sheet, Package, Warehouse as WhIcon, Factory, Search, X } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Pager, { usePagination } from "@/components/Pager";
import { useAuth } from "@/context/AuthContext";

function AllocBadge({ alloc }) {
  return alloc === "warehouse" ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase border border-[#0055FF] text-[#0055FF]"><WhIcon size={10} /> Depo</span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase border border-[#FF3B30] text-[#FF3B30]"><Factory size={10} /> Üretim</span>
  );
}

function IssueDialog({ order, onIssued, canIssue = true }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [qtyMap, setQtyMap] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const openDialog = () => {
    const sel = {};
    const qm = {};
    order.ready_items.forEach((it) => {
      sel[it.index] = true;
      qm[it.index] = it.available_qty ?? it.quantity ?? 0;
    });
    setSelected(sel);
    setQtyMap(qm);
    setNotes("");
    setOpen(true);
  };

  const toggle = (idx) => setSelected((s) => ({ ...s, [idx]: !s[idx] }));

  const setQty = (idx, val, maxAvailable) => {
    const num = Math.max(0, Math.min(Number(val) || 0, maxAvailable));
    setQtyMap((m) => ({ ...m, [idx]: num }));
  };

  const total = order.ready_items
    .filter((it) => selected[it.index])
    .reduce((sum, it) => sum + Number(qtyMap[it.index] || 0) * Number(it.unit_price || 0), 0);

  const submit = async () => {
    const items = order.ready_items
      .filter((it) => selected[it.index] && Number(qtyMap[it.index] || 0) > 0)
      .map((it) => ({ index: it.index, qty: Number(qtyMap[it.index]) }));
    if (items.length === 0) { toast.error("En az bir kalem ve miktar seçin"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/waybills", { order_id: order.id, items, notes });
      toast.success(`${data.waybill_number} oluşturuldu`);
      setOpen(false);
      onIssued?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <>
      {canIssue && (
        <Button
          className="rounded-none bg-[#09090B] hover:bg-[#00A650]"
          onClick={openDialog}
          data-testid={`issue-waybill-btn-${order.id}`}
        >
          <Receipt size={14} className="mr-2" /> İrsaliye Kes
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-2 border-[#09090B] bg-white sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">
              İrsaliye · {order.order_number}
            </DialogTitle>
            <div className="text-sm text-neutral-600">{order.customer_name}</div>
          </DialogHeader>

          <div className="border-2 border-[#09090B]">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="font-mono uppercase text-xs">SKU</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Ürün</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Kaynak</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">Hazır</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">Birim Fiyat</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">Sevk Miktar</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">Tutar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {order.ready_items.map((it) => {
                  const available = Number(it.available_qty ?? it.quantity ?? 0);
                  const currentQty = Number(qtyMap[it.index] ?? 0);
                  const lineTotal = currentQty * Number(it.unit_price || 0);
                  const itemNote = it.notes || it.split_note || "";
                  return (
                    <React.Fragment key={it.index}>
                    <TableRow data-testid={`waybill-item-${order.id}-${it.index}`}>
                      <TableCell>
                        <Checkbox
                          checked={!!selected[it.index]}
                          onCheckedChange={() => toggle(it.index)}
                          className="w-5 h-5 rounded-none border-2 border-[#09090B]"
                          data-testid={`waybill-check-${order.id}-${it.index}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{it.sku}</TableCell>
                      <TableCell className="font-semibold">{it.product_name}</TableCell>
                      <TableCell><AllocBadge alloc={it.allocation} /></TableCell>
                      <TableCell className="text-right font-mono text-xs text-neutral-600">
                        {available} {it.unit}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm" data-testid={`waybill-unit-price-${order.id}-${it.index}`}>
                        {fmtCurrency(Number(it.unit_price || 0))}
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          min={0}
                          max={available}
                          step="any"
                          value={qtyMap[it.index] ?? 0}
                          disabled={!selected[it.index]}
                          onChange={(e) => setQty(it.index, e.target.value, available)}
                          data-testid={`waybill-qty-${order.id}-${it.index}`}
                          className="rounded-none border-2 border-[#09090B] w-24 h-9 text-right font-mono ml-auto focus-visible:ring-0"
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono">{fmtCurrency(lineTotal)}</TableCell>
                    </TableRow>
                    {itemNote && (
                      <TableRow className="bg-neutral-50 border-b-2 border-[#09090B]" data-testid={`waybill-item-note-${order.id}-${it.index}`}>
                        <TableCell></TableCell>
                        <TableCell colSpan={7} className="py-1.5">
                          <span className="font-bold uppercase tracking-widest text-[9px] text-neutral-500 mr-1">Kalem Notu:</span>
                          <span className="text-xs font-mono whitespace-pre-wrap">{itemNote}</span>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {order.notes && (
            <div
              className="bg-amber-50 border-l-4 border-[#FF9500] px-3 py-2"
              data-testid={`waybill-order-notes-${order.id}`}
            >
              <div className="text-[9px] font-mono font-bold uppercase tracking-widest text-[#B45309] mb-1">Sipariş Notu</div>
              <div className="text-xs font-mono whitespace-pre-wrap">{order.notes}</div>
            </div>
          )}

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-neutral-500">Notlar</label>
            <Textarea
              value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
              data-testid="waybill-notes"
              placeholder="Örn. sevkiyat aracı, plaka, sürücü..."
            />
          </div>

          <div className="flex items-center justify-between border-t-2 border-[#09090B] pt-3">
            <div className="text-xs font-mono uppercase text-neutral-500">Seçili toplam</div>
            <div className="text-2xl font-mono font-bold" data-testid="waybill-selected-total">{fmtCurrency(total)}</div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => setOpen(false)}>İptal</Button>
            <Button className="rounded-none bg-[#09090B] hover:bg-[#00A650]" onClick={submit} disabled={busy} data-testid="waybill-submit-btn">
              <Receipt size={16} className="mr-2" /> {busy ? "Oluşturuluyor…" : "İrsaliye Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Accounting() {
  const { user } = useAuth();
  const isPrivileged = user && (user.role === "admin" || user.role === "sub_admin");
  // Satış rolü ekranı görür ama irsaliye kesemez ve kesilen listeyi göremez.
  const canIssueWaybill = user && (user.role === "accounting" || user.role === "admin");
  const [pendingOrders, setPendingOrders] = useState([]);
  const [waybills, setWaybills] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setRefreshing(true);
    try {
      const requests = [api.get(`/waybills/pending-orders?_ts=${Date.now()}`)];
      if (canIssueWaybill) requests.push(api.get(`/waybills?_ts=${Date.now()}`));
      const results = await Promise.all(requests);
      setPendingOrders(results[0].data);
      setWaybills(canIssueWaybill ? results[1].data : []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadPdf = async (wb) => {
    try {
      const res = await api.get(`/waybills/${wb.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${wb.waybill_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadXlsx = async (wb) => {
    try {
      const res = await api.get(`/waybills/${wb.id}/xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${wb.waybill_number}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const norm = (s) => (s || "").toLocaleLowerCase("tr-TR").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ı/g, "i");
  const q = norm(search.trim());
  const filteredPending = useMemo(() => (q ? pendingOrders.filter((o) => norm(o.customer_name).includes(q)) : pendingOrders), [pendingOrders, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const filteredWaybills = useMemo(() => (q ? waybills.filter((w) => norm(w.customer_name).includes(q)) : waybills), [waybills, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingPagination = usePagination(filteredPending, 4);
  const waybillsPagination = usePagination(filteredWaybills, 10);

  return (
    <div className="space-y-6" data-testid="accounting-page">
      <PageHeader
        kicker="Muhasebe · Sevkiyat"
        title="MUHASEBE"
        subtitle="Depoda hazırlanan veya üretimde tamamlanan kalemler için irsaliye kesin."
        border="border-[#00A650]"
        onRefresh={load}
        refreshing={refreshing}
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-4 py-2 border-2 border-[#00A650] text-[#00A650] font-mono uppercase text-xs" data-testid="pending-count-box">
              <Receipt size={16} /> Bekleyen: {pendingOrders.length}
            </div>
            {isPrivileged && (
              <div className="flex items-center gap-2 px-4 py-2 border-2 border-[#00A650] text-[#00A650] font-mono uppercase text-xs" data-testid="pending-amount-box">
                Bekleyen Tutar: {fmtCurrency(pendingOrders.reduce((sum, o) => sum + (Number(o.pending_amount) || 0), 0))}
              </div>
            )}
          </div>
        }
      />

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Müşteri adına göre ara…"
          className="rounded-none border-2 border-[#09090B] pl-9 pr-9 bg-white" data-testid="accounting-customer-search" />
        {search && (
          <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-neutral-500 hover:text-[#09090B]"
            title="Temizle" data-testid="accounting-customer-search-clear">
            <X size={14} />
          </button>
        )}
      </div>

      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="rounded-none border-2 border-[#09090B] bg-white h-auto p-0">
          <TabsTrigger
            value="pending"
            data-testid="tab-pending"
            className="rounded-none px-6 py-3 data-[state=active]:bg-[#09090B] data-[state=active]:text-white font-mono uppercase text-xs"
          >
            İrsaliye Bekleyenler ({q ? `${filteredPending.length}/${pendingOrders.length}` : pendingOrders.length})
          </TabsTrigger>
          {canIssueWaybill && (
            <TabsTrigger
              value="issued"
              data-testid="tab-issued"
              className="rounded-none px-6 py-3 data-[state=active]:bg-[#09090B] data-[state=active]:text-white font-mono uppercase text-xs"
            >
              Kesilen İrsaliyeler ({q ? `${filteredWaybills.length}/${waybills.length}` : waybills.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="pending" className="mt-4 space-y-4">
          {pendingPagination.paged.map((o) => (
            <div key={o.id} className="bg-white border-2 border-[#09090B]" data-testid={`pending-order-${o.id}`}>
              <div className="p-4 border-b-2 border-[#09090B] flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-mono uppercase text-neutral-500">{fmtDate(o.created_at)}</div>
                  <div className="text-lg font-mono font-bold">{o.order_number}</div>
                  <div className="text-sm">{o.customer_name}</div>
                  {o.payment_info && (
                    <div
                      className="mt-1 inline-block text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-neutral-400 text-neutral-700"
                      data-testid={`accounting-payment-${o.id}`}
                    >
                      Ödeme: {o.payment_info}
                    </div>
                  )}
                  {o.notes && (
                    <div
                      className="mt-2 text-xs font-mono bg-amber-50 border-l-4 border-[#FF9500] px-2 py-1 whitespace-pre-wrap"
                      data-testid={`accounting-order-notes-${o.id}`}
                    >
                      <span className="font-bold uppercase tracking-widest text-[9px] text-[#B45309] mr-1">Sipariş Notu:</span>
                      {o.notes}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-[10px] font-mono uppercase text-neutral-500">Kalan Tutar</div>
                    <div className="text-lg font-mono font-bold" data-testid={`pending-remaining-amount-${o.id}`}>
                      {fmtCurrency(o.pending_amount ?? o.remaining_amount ?? o.subtotal)}
                    </div>
                  </div>
                  <IssueDialog order={o} onIssued={load} canIssue={canIssueWaybill} />
                </div>
              </div>
              <div>
                {o.ready_items.map((it) => {
                  const avail = it.available_qty ?? it.quantity;
                  const itemNote = it.notes || it.split_note || "";
                  return (
                    <div key={it.index} className="p-3 border-t border-neutral-200 first:border-t-0" data-testid={`ready-item-${o.id}-${it.index}`}>
                      <div className="flex items-center gap-3">
                        <Package size={14} className="text-neutral-400" />
                        <div className="flex-1">
                          <div className="font-semibold text-sm">{it.product_name}</div>
                          <div className="text-xs font-mono text-neutral-500">{it.sku}</div>
                        </div>
                        <AllocBadge alloc={it.allocation} />
                        <div className="text-xs font-mono w-24 text-right text-neutral-600" data-testid={`ready-item-unit-price-${o.id}-${it.index}`}>
                          <div className="text-[9px] uppercase text-neutral-400">Birim</div>
                          {fmtCurrency(Number(it.unit_price || 0))}
                        </div>
                        <div className="text-sm font-mono w-32 text-right">
                          <span className="text-[#00A650] font-bold">{avail}</span>
                          <span className="text-neutral-400"> / {it.quantity} {it.unit}</span>
                        </div>
                        <div className="text-sm font-mono font-bold w-28 text-right">
                          {fmtCurrency(Number(avail) * Number(it.unit_price || 0))}
                        </div>
                      </div>
                      {itemNote && (
                        <div
                          className="mt-2 ml-6 text-xs font-mono text-neutral-700 bg-neutral-50 border-l-2 border-neutral-400 px-2 py-1 whitespace-pre-wrap"
                          data-testid={`ready-item-notes-${o.id}-${it.index}`}
                        >
                          <span className="font-bold uppercase tracking-widest text-[9px] text-neutral-500 mr-1">Kalem Notu:</span>
                          {itemNote}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {filteredPending.length === 0 && (
            <div className="bg-white border-2 border-dashed border-[#09090B] p-12 text-center text-neutral-500" data-testid="accounting-pending-empty">
              {q ? `"${search}" ile eşleşen müşteri bulunamadı.` : "İrsaliye bekleyen kalem yok."}
            </div>
          )}
          <Pager {...pendingPagination} testid="acc-pending-pager" />
        </TabsContent>

        <TabsContent value="issued" className="mt-4">
          <div className="bg-white border-2 border-[#09090B]">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
                  <TableHead className="font-mono uppercase text-xs">İrsaliye No</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Tarih</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Sipariş</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Müşteri</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">Kalem</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">Tutar</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">PDF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {waybillsPagination.paged.map((w) => (
                  <TableRow key={w.id} data-testid={`waybill-row-${w.id}`}>
                    <TableCell className="font-mono font-bold">{w.waybill_number}</TableCell>
                    <TableCell className="text-sm">{fmtDateTime(w.created_at)}</TableCell>
                    <TableCell className="font-mono text-sm">{w.order_number}</TableCell>
                    <TableCell className="font-semibold">{w.customer_name}</TableCell>
                    <TableCell className="text-right font-mono">{w.items.length}</TableCell>
                    <TableCell className="text-right font-mono">{fmtCurrency(w.total)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(w)} data-testid={`waybill-pdf-${w.id}`} title="PDF">
                        <FileDown size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadXlsx(w)} data-testid={`waybill-xlsx-${w.id}`} title="Excel" className="ml-1">
                        <Sheet size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredWaybills.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-neutral-500" data-testid="accounting-waybills-empty">{q ? `"${search}" ile eşleşen müşteri bulunamadı.` : "Henüz irsaliye kesilmedi"}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <Pager {...waybillsPagination} testid="acc-waybill-pager" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
